// A field is defined once: forms use its label/type/options and tables project it.
// table:false excludes a field entirely; table.hidden keeps it in the column chooser.
export function defineFormSchema({ id, fields }) {
  const names = new Set();
  const normalized = fields.map((field) => {
    if (!field.name || names.has(field.name)) throw new Error(`Campo duplicato o senza nome in ${id}`);
    names.add(field.name);
    return { type: 'text', ...field };
  });
  return {
    id,
    fields: normalized,
    field(name) {
      const field = normalized.find((item) => item.name === name);
      if (!field) throw new Error(`Campo ${name} non definito in ${id}`);
      return field;
    },
  };
}

export function getFieldValue(row, accessor) {
  if (typeof accessor === 'function') return accessor(row);
  return String(accessor || '')
    .split('.')
    .reduce((value, key) => value?.[key], row);
}

export function getOptionLabel(value, options = []) {
  const option = options.find((item) => String(item?.value ?? item) === String(value));
  return option?.label ?? option ?? value;
}

export function numericValue(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

export function dateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // Date-only form values are calendar dates, not UTC timestamps.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value));
  if (match) {
    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
      ? date
      : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatFieldValue(value, column) {
  if (value === null || value === undefined || value === '') return column.emptyValue ?? '—';
  const type = String(column.type || 'text').toLowerCase();
  if (type === 'boolean') return value === true || value === 'true' || value === 1 ? 'Sì' : 'No';
  if (type === 'date' || type === 'datetime-local' || type === 'datetime') {
    const date = dateValue(value);
    return date
      ? new Intl.DateTimeFormat('it-IT', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          ...(type !== 'date' ? { hour: '2-digit', minute: '2-digit' } : {}),
        }).format(date)
      : '—';
  }
  if (type === 'currency' || type === 'number') {
    let number = numericValue(value);
    if (number === null) return '—';
    if (column.direction === 'expense') number = -Math.abs(number);
    if (column.direction === 'income') number = Math.abs(number);
    return new Intl.NumberFormat(
      'it-IT',
      type === 'currency'
        ? {
            style: 'currency',
            currency: column.currency || 'EUR',
            signDisplay: column.direction === 'income' ? 'exceptZero' : 'auto',
          }
        : { maximumFractionDigits: 6 }
    ).format(number);
  }
  if (Array.isArray(value)) return value.map((item) => getOptionLabel(item, column.options)).join(', ');
  const label = getOptionLabel(value, column.options);
  return typeof label === 'object' ? String(label.label ?? label.name ?? '—') : String(label);
}

export function customFieldsToSchema(definitions = []) {
  return definitions
    .filter((field) => field && field.archived !== true && field.key)
    .slice()
    .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0))
    .map((field) => ({
      ...field,
      name: `customFields.${field.key}`,
      type: String(field.type || 'text').toLowerCase(),
      // Read the key literally; a custom key can contain a dot.
      table:
        field.table === false ? false : { accessor: (row) => row.customFields?.[field.key], ...field.table },
    }));
}

export function columnsFromFormSchema(schema, { customFields = [], overrides = [] } = {}) {
  const fields = [...(schema?.fields || []), ...customFieldsToSchema(customFields)];
  const excluded = new Set(
    fields
      .filter(
        (field) =>
          field.table === false || field.sensitive || ['password', 'hidden', 'file'].includes(field.type)
      )
      .map((field) => field.name)
  );
  const columns = fields
    .filter((field) => !excluded.has(field.name))
    .map((field, index) => {
      const table = field.table || {};
      return {
        id: field.name,
        header: field.label,
        accessor: field.name,
        type: field.type,
        options: field.options,
        primary: field.primary,
        sortable: true,
        hidden: field.type === 'textarea',
        order: index,
        ...table,
        ...overrides.find((column) => column.id === field.name),
      };
    });
  return [
    ...columns,
    ...overrides.filter(
      (column) => !fields.some((field) => field.name === column.id) && !excluded.has(column.id)
    ),
  ].sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
}

export function sortTableRows(rows, columns, sort) {
  const column = columns.find((item) => item.id === sort?.id);
  if (!column || !sort?.direction) return rows;
  const value = (row) => {
    const raw = getFieldValue(row, column.sortValue || column.accessor || column.id);
    if (raw == null || raw === '') return null;
    if (['number', 'currency'].includes(column.type)) {
      const number = numericValue(raw);
      return number == null
        ? null
        : column.direction === 'expense'
          ? -Math.abs(number)
          : column.direction === 'income'
            ? Math.abs(number)
            : number;
    }
    if (['date', 'datetime', 'datetime-local'].includes(column.type))
      return dateValue(raw)?.getTime() ?? null;
    return getOptionLabel(raw, column.options);
  };
  const collator = new Intl.Collator('it-IT', { numeric: true, sensitivity: 'base' });
  return rows
    .map((row, index) => ({ row, index, value: value(row) }))
    .sort((a, b) => {
      if (a.value == null || b.value == null)
        return a.value == null && b.value == null ? a.index - b.index : a.value == null ? 1 : -1;
      const result =
        typeof a.value === 'number' && typeof b.value === 'number'
          ? a.value - b.value
          : collator.compare(String(a.value), String(b.value));
      return result ? result * (sort.direction === 'desc' ? -1 : 1) : a.index - b.index;
    })
    .map((item) => item.row);
}
